import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { SUBMISSION_STATUS_LABEL } from "@colab/shared";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatDate } from "@/lib/utils";
import { DecisionPanel } from "./decision-panel";
import { SubmissionPayload } from "./submission-payload";
import { StatusHistoryTimeline } from "./status-history-timeline";
import type { AdminSubmissionDetail } from "./types";

// Sheet body — composes the 3 panels: DecisionPanel (status-aware
// Approve/Decline), SubmissionPayload (read-only renderer over the
// snapshotted definition), StatusHistoryTimeline (status_history events).
//
// Mirror of components/admin/submissions/SubmissionDrawerContent.tsx with
// Drawer → Sheet (the SPA's right-side overlay primitive) and `<Link>`
// from react-router instead of next/link.

export function SubmissionDrawerContent({
  submission,
}: {
  submission: AdminSubmissionDetail;
}) {
  const downstreamHref =
    submission.downstream_kind === "deal" && submission.downstream_id
      ? `/admin/deals?deal=${submission.downstream_id}`
      : null;

  return (
    <>
      <SheetHeader className="border-b">
        <SheetTitle className="font-display text-lg font-semibold tracking-tight">
          {submission.form_name}
        </SheetTitle>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-medium">
            {SUBMISSION_STATUS_LABEL[submission.status]}
          </span>
          <span>·</span>
          <span>
            Submitted {formatDate(submission.submitted_at)}
            {submission.partner_name ? ` by ${submission.partner_name}` : ""}
          </span>
        </div>
        {downstreamHref ? (
          <Link
            to={downstreamHref}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            Open {submission.downstream_kind}
            <ArrowRight className="h-3 w-3" />
          </Link>
        ) : null}
      </SheetHeader>

      <div className="space-y-6 overflow-y-auto px-6 py-5">
        <DecisionPanel
          submissionId={submission.id}
          status={submission.status}
          existingNote={submission.decision_note}
        />

        <section>
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Submission
          </h3>
          <SubmissionPayload
            definition={submission.definition}
            payload={submission.payload}
          />
          {submission.submitter_email || submission.submitter_name ? (
            <div className="mt-4 rounded-md border bg-muted/30 p-3 text-xs">
              <div className="font-medium text-foreground">Submitted by</div>
              <div className="text-muted-foreground">
                {submission.submitter_name ?? "Anonymous"}
                {submission.submitter_email
                  ? ` · ${submission.submitter_email}`
                  : ""}
              </div>
            </div>
          ) : null}
        </section>

        <section>
          <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Activity
          </h3>
          <StatusHistoryTimeline
            entries={submission.status_history}
            decidedByName={submission.decided_by_name}
            decisionNote={submission.decision_note}
          />
        </section>
      </div>
    </>
  );
}
